import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { API, authHeaders, formatErr, newId } from "./api";
import { inputCls, SaveBar, TabCard, SortableList, SortableItem, DeleteButton } from "./fields";

const PAGES = [
  { key: "home", label: "Home" },
  { key: "brand-website", label: "Brand & Website" },
  { key: "automation", label: "WhatsApp & Automation" },
  { key: "sops", label: "SOPs" },
];

export default function FaqsTab({ content, token, onSaved, onUnauthorized }) {
  const [faqs, setFaqs] = useState(content.faqs);
  const [page, setPage] = useState("home");
  const [saving, setSaving] = useState(false);
  useEffect(() => setFaqs(content.faqs), [content.faqs]);

  const items = faqs[page] || [];
  const setItems = (next) => setFaqs({ ...faqs, [page]: typeof next === "function" ? next(items) : next });
  const update = (id, patch) => setItems(items.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const remove = (id) => setItems(items.filter((f) => f.id !== id));

  const save = async () => {
    for (const [pg, list] of Object.entries(faqs)) {
      for (const f of list) {
        if (!f.q.trim() || !f.a.trim()) return toast.error(`Empty question or answer in ${pg}.`);
      }
    }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/content/faqs`, { faqs }, authHeaders(token));
      onSaved(data);
      toast.success("Saved — live on the website now.");
    } catch (err) {
      if (err.response?.status === 401) return onUnauthorized();
      toast.error(formatErr(err, "Save failed. Try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <TabCard title="FAQs" sub="Per page — FAQ search schema (JSON-LD) is generated automatically" testid="tab-faqs">
      <div className="mb-6 flex flex-wrap gap-2" role="tablist" aria-label="FAQ pages">
        {PAGES.map((p) => (
          <button
            key={p.key}
            onClick={() => setPage(p.key)}
            data-testid={`faq-page-${p.key}`}
            className={`border px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
              page === p.key ? "border-myrtle bg-myrtle/10 text-pop" : "border-line text-mut hover:text-txt"
            }`}
          >
            {p.label} ({(faqs[p.key] || []).length})
          </button>
        ))}
      </div>

      <SortableList items={items} onReorder={setItems} testid={`faq-list-${page}`}>
        {(f) => (
          <SortableItem key={f.id} item={f} testid={`faq-edit-${f.id}`}>
            <div className="grid gap-3">
              <input value={f.q} onChange={(e) => update(f.id, { q: e.target.value })} placeholder="Question" data-testid={`faq-q-${f.id}`} className={inputCls} />
              <textarea rows={2} value={f.a} onChange={(e) => update(f.id, { a: e.target.value })} placeholder="Answer" data-testid={`faq-a-${f.id}`} className={`${inputCls} resize-none`} />
              <div className="flex justify-end"><DeleteButton onClick={() => remove(f.id)} testid={`faq-delete-${f.id}`} /></div>
            </div>
          </SortableItem>
        )}
      </SortableList>
      <button
        type="button"
        onClick={() => setItems([...items, { id: newId(), q: "", a: "" }])}
        data-testid="faq-add"
        className="mt-4 flex items-center gap-2 border border-dashed border-line px-5 py-3 text-xs uppercase tracking-[0.15em] text-mut transition-colors duration-300 hover:border-myrtle hover:text-pop"
      >
        <Plus size={14} /> Add Question
      </button>

      <SaveBar onSave={save} saving={saving} lastSaved={content.saved_at?.faqs} testid="faqs-save" />
    </TabCard>
  );
}
